import React, { useMemo, useState } from 'react';
import {
  Box, Stack, FormControl, FormLabel, FormHelperText, Input, Select, Option,
  Switch, Sheet, Divider, Typography, Chip
} from '@mui/joy';
import type { SmtpFingerprint } from '../../api/admin.ts';
import ChipsInput from './ChipsInput.tsx';

export interface SmtpPreset {
  id: string;
  label: string;
  description: string;
  fingerprint: Partial<SmtpFingerprint>;
}

/**
 * Common SMTP daemon fingerprints. Each preset overrides only fields that
 * are part of that MTA's identity — the rest stay on whatever the admin
 * configured. Pick one to instantly blend into a popular MTA.
 */
export const SMTP_PRESETS: SmtpPreset[] = [
  {
    id: 'postfix-ubuntu',
    label: 'Postfix (Ubuntu)',
    description: 'Most common Linux MTA — Ubuntu/Debian default banner.',
    fingerprint: {
      banner: 'Postfix (Ubuntu)',
      greetingOverride: '',
      ehloSuffix: '',
      ehloFeatures: [
        'PIPELINING', 'SIZE 10485760', 'ETRN', 'STARTTLS',
        'ENHANCEDSTATUSCODES', '8BITMIME', 'DSN', 'SMTPUTF8', 'CHUNKING'
      ],
      okResponse: '2.0.0 Ok',
      mailAcceptedResponse: '2.1.0 Ok',
      rcptAcceptedResponse: '2.1.5 Ok',
      dataQueuedResponse: '2.0.0 Ok: queued as {id}',
      byeResponse: '2.0.0 Bye',
      startTlsResponse: '2.0.0 Ready to start TLS',
      vrfyCode: 252, vrfyMessage: '2.5.2 Send some mail, I will try my best',
      helpCode: 502, helpMessage: '5.5.1 Error: command not implemented',
      tooSoonResponse: '5.5.1 Error: bad syntax',
      unknownCommandResponse: '5.5.2 Error: command not recognized',
      hideXClient: true
    }
  },
  {
    id: 'postfix-debian',
    label: 'Postfix (Debian)',
    description: 'Plain Debian banner.',
    fingerprint: {
      banner: 'Postfix (Debian/GNU)',
      greetingOverride: '', ehloSuffix: '',
      ehloFeatures: [
        'PIPELINING', 'SIZE 10485760', 'ETRN', 'STARTTLS',
        'ENHANCEDSTATUSCODES', '8BITMIME', 'DSN', 'SMTPUTF8', 'CHUNKING'
      ]
    }
  },
  {
    id: 'postfix-centos',
    label: 'Postfix (CentOS/RHEL)',
    description: 'Bare Postfix banner — CentOS/RHEL default.',
    fingerprint: {
      banner: 'Postfix',
      greetingOverride: '', ehloSuffix: '',
      ehloFeatures: [
        'PIPELINING', 'SIZE 10485760', 'ETRN', 'STARTTLS',
        'ENHANCEDSTATUSCODES', '8BITMIME', 'DSN', 'SMTPUTF8', 'CHUNKING'
      ]
    }
  },
  {
    id: 'exim',
    label: 'Exim 4.96 (cPanel)',
    description: 'Exim banner used by most cPanel hosts.',
    fingerprint: {
      banner: '',
      greetingOverride: '{host} ESMTP Exim 4.96 #2 {date}',
      ehloSuffix: 'Hello {client}',
      ehloFeatures: [
        'SIZE 52428800', 'PIPELINING', '8BITMIME', 'CHUNKING',
        'STARTTLS', 'PRDR', 'HELP'
      ],
      okResponse: 'OK',
      mailAcceptedResponse: 'OK',
      rcptAcceptedResponse: 'Accepted',
      dataQueuedResponse: 'OK id={id}',
      byeResponse: '{host} closing connection',
      vrfyCode: 252, vrfyMessage: 'send some mail, i\'ll try my best',
      helpCode: 214, helpMessage: 'Commands supported: AUTH STARTTLS HELO EHLO MAIL RCPT DATA NOOP QUIT RSET HELP',
      tooSoonResponse: 'SMTP synchronization error',
      unknownCommandResponse: 'unrecognized command',
      hideXClient: true
    }
  },
  {
    id: 'sendmail',
    label: 'Sendmail 8.15.2',
    description: 'Classic Sendmail banner.',
    fingerprint: {
      banner: '',
      greetingOverride: '{host} ESMTP Sendmail 8.15.2/8.15.2; {date}',
      ehloSuffix: 'Hello {client}, pleased to meet you',
      ehloFeatures: [
        'ENHANCEDSTATUSCODES', 'PIPELINING', '8BITMIME', 'SIZE 36700160',
        'DSN', 'ETRN', 'STARTTLS', 'DELIVERBY', 'HELP'
      ],
      okResponse: '2.0.0 Reset state',
      mailAcceptedResponse: '2.1.0 Sender ok',
      rcptAcceptedResponse: '2.1.5 Recipient ok',
      dataQueuedResponse: '2.0.0 {id} Message accepted for delivery',
      byeResponse: '2.0.0 {host} closing connection',
      vrfyCode: 252, vrfyMessage: '2.5.2 Cannot VRFY user; try RCPT to attempt delivery (or try finger)',
      helpCode: 214, helpMessage: '2.0.0 See https://www.sendmail.com/email/support',
      tooSoonResponse: '4.7.0 client host rejected for greeting',
      unknownCommandResponse: '5.5.1 Command unrecognized',
      hideXClient: true
    }
  },
  {
    id: 'google',
    label: 'Google (mx.google.com)',
    description: 'Mimics Gmail edge MX banner.',
    fingerprint: {
      banner: '- gsmtp',
      greetingOverride: '',
      ehloSuffix: 'at your service, {client}',
      ehloFeatures: [
        'SIZE 157286400', '8BITMIME', 'STARTTLS', 'ENHANCEDSTATUSCODES',
        'PIPELINING', 'CHUNKING', 'SMTPUTF8'
      ],
      okResponse: '2.0.0 OK gsmtp',
      mailAcceptedResponse: '2.1.0 OK gsmtp',
      rcptAcceptedResponse: '2.1.5 OK gsmtp',
      dataQueuedResponse: '2.0.0 OK  {id} - gsmtp',
      byeResponse: '2.0.0 closing connection {id} - gsmtp',
      startTlsResponse: '2.0.0 Ready to start TLS',
      vrfyCode: 252, vrfyMessage: '2.1.5 Send some mail, I\'ll try my best',
      helpCode: 214, helpMessage: '2.0.0  https://www.google.com/search?btnI&q=RFC+5321',
      tooSoonResponse: '5.5.1 Protocol error',
      unknownCommandResponse: '5.5.1 Unrecognized command. Learn more at https://support.google.com/mail/?p=RfcMessageNonCompliant',
      hideXClient: true
    }
  },
  {
    id: 'microsoft',
    label: 'Microsoft ESMTP (Exchange / outlook.com)',
    description: 'Mimics Microsoft Exchange / Outlook front-door banner.',
    fingerprint: {
      banner: '',
      greetingOverride: '{host} Microsoft ESMTP MAIL Service ready at {date}',
      ehloSuffix: 'Hello [{client}]',
      ehloFeatures: [
        'SIZE 157286400', 'PIPELINING', 'DSN', 'ENHANCEDSTATUSCODES',
        'STARTTLS', '8BITMIME', 'BINARYMIME', 'CHUNKING', 'SMTPUTF8'
      ],
      okResponse: '2.0.0 Resetting',
      mailAcceptedResponse: '2.1.0 Sender OK',
      rcptAcceptedResponse: '2.1.5 Recipient OK',
      dataQueuedResponse: '2.6.0 <{id}@{host}> [InternalId={id}] Queued mail for delivery',
      byeResponse: '2.0.0 Service closing transmission channel',
      vrfyCode: 252, vrfyMessage: '2.1.5 Cannot VRFY user, but will accept message and attempt delivery',
      helpCode: 214, helpMessage: '2.0.0 https://aka.ms/exchange',
      tooSoonResponse: '5.5.2 Send hello first',
      unknownCommandResponse: '5.3.3 Unrecognized command',
      hideXClient: true
    }
  },
  {
    id: 'haraka',
    label: 'Haraka 3.0',
    description: 'Node.js Haraka MTA — a common modern fingerprint.',
    fingerprint: {
      banner: 'Haraka/3.0.2 ready',
      greetingOverride: '', ehloSuffix: '',
      ehloFeatures: [
        'PIPELINING', '8BITMIME', 'SIZE 26214400', 'STARTTLS', 'ENHANCEDSTATUSCODES'
      ]
    }
  },
  {
    id: 'minimal',
    label: 'Minimal (no banner)',
    description: 'Just the hostname and ESMTP keyword.',
    fingerprint: {
      banner: '',
      greetingOverride: '', ehloSuffix: '',
      ehloFeatures: ['PIPELINING', '8BITMIME', 'SMTPUTF8', 'STARTTLS', 'SIZE 10485760']
    }
  }
];

interface Props {
  value: SmtpFingerprint;
  onChange: (next: SmtpFingerprint) => void;
}

export default function SmtpFingerprintEditor({ value, onChange }: Props) {
  const [presetId, setPresetId] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const upd = <K extends keyof SmtpFingerprint>(k: K, v: SmtpFingerprint[K]) => {
    onChange({ ...value, [k]: v });
  };

  const applyPreset = (id: string) => {
    const p = SMTP_PRESETS.find(x => x.id === id);
    if (!p) return;
    setPresetId(id);
    onChange({ ...value, ...p.fingerprint });
  };

  const currentBannerPreview = useMemo(() => {
    const host = value.hostname || '<your-hostname>';
    if (value.greetingOverride) {
      return value.greetingOverride
        .replace(/\{host\}/g, host)
        .replace(/\{date\}/g, new Date().toUTCString());
    }
    return `${host} ESMTP${value.banner ? ' ' + value.banner : ''}`;
  }, [value]);

  return (
    <Stack spacing={2}>
      <Sheet variant="soft" color="warning" sx={{ p: 1.5, borderRadius: 'sm' }}>
        <Typography level="body-sm">
          The default <code>smtp-server</code> npm package emits unique strings
          ("Hi there", "Nice to meet you", "You are no wizard!", etc.) that
          reputation services use to fingerprint and shadowban this codebase.
          Pick a preset below — values are stored in the DB and applied to
          new SMTP connections instantly, no restart required.
        </Typography>
      </Sheet>

      <FormControl>
        <FormLabel>Preset</FormLabel>
        <Select
          value={presetId}
          placeholder="Choose a preset to fill all fields…"
          onChange={(_, v) => v && applyPreset(v as string)}
        >
          {SMTP_PRESETS.map(p => (
            <Option key={p.id} value={p.id}>
              {p.label}
            </Option>
          ))}
        </Select>
        {presetId && (
          <FormHelperText>{SMTP_PRESETS.find(p => p.id === presetId)?.description}</FormHelperText>
        )}
      </FormControl>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Hostname</FormLabel>
          <Input
            value={value.hostname}
            onChange={e => upd('hostname', e.target.value)}
            placeholder="leave blank to use config.domain"
          />
          <FormHelperText>Used in 220 banner and EHLO greetings.</FormHelperText>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Banner (after ESMTP)</FormLabel>
          <Input
            value={value.banner}
            onChange={e => upd('banner', e.target.value)}
            placeholder="Postfix"
            disabled={!!value.greetingOverride}
          />
          <FormHelperText>e.g. <code>Postfix (Ubuntu)</code> or <code>- gsmtp</code>.</FormHelperText>
        </FormControl>
      </Stack>

      <FormControl>
        <FormLabel>Custom 220 greeting (overrides banner)</FormLabel>
        <Input
          value={value.greetingOverride}
          onChange={e => upd('greetingOverride', e.target.value)}
          placeholder="e.g. {host} Microsoft ESMTP MAIL Service ready at {date}"
        />
        <FormHelperText>
          Placeholders: <code>{'{host}'}</code>, <code>{'{date}'}</code>.
          Use this to mimic banners that don't follow the standard "ESMTP …" prefix.
        </FormHelperText>
      </FormControl>

      <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
        <Typography level="body-xs" textColor="text.tertiary">Live preview of 220 greeting</Typography>
        <Typography level="body-sm" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
          <Chip size="sm" variant="soft" sx={{ mr: 1 }}>220</Chip>
          {currentBannerPreview}
        </Typography>
      </Sheet>

      <Divider />

      <Stack direction="row" alignItems="center" spacing={1}>
        <Switch checked={showAdvanced} onChange={e => setShowAdvanced(e.target.checked)} />
        <Typography level="body-sm">Show advanced response strings</Typography>
      </Stack>

      {showAdvanced && (
        <Stack spacing={2}>
          <FormControl>
            <FormLabel>EHLO greeting suffix (after hostname)</FormLabel>
            <Input
              value={value.ehloSuffix}
              onChange={e => upd('ehloSuffix', e.target.value)}
              placeholder="empty for bare hostname (Postfix style)"
            />
            <FormHelperText>
              Placeholder: <code>{'{client}'}</code>. e.g. <code>at your service, {'{client}'}</code> (Google).
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>EHLO capabilities</FormLabel>
            <ChipsInput
              label=""
              values={value.ehloFeatures}
              onChange={v => upd('ehloFeatures', v)}
              placeholder="PIPELINING, SIZE 10485760, …"
            />
            <FormHelperText>
              Order is preserved. STARTTLS / AUTH are only advertised when the connection actually supports them.
            </FormHelperText>
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>OK response (NOOP / RSET)</FormLabel>
              <Input value={value.okResponse} onChange={e => upd('okResponse', e.target.value)} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>QUIT (Bye) response</FormLabel>
              <Input value={value.byeResponse} onChange={e => upd('byeResponse', e.target.value)} />
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>MAIL FROM accepted</FormLabel>
              <Input value={value.mailAcceptedResponse} onChange={e => upd('mailAcceptedResponse', e.target.value)} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>RCPT TO accepted</FormLabel>
              <Input value={value.rcptAcceptedResponse} onChange={e => upd('rcptAcceptedResponse', e.target.value)} />
            </FormControl>
          </Stack>

          <FormControl>
            <FormLabel>Data queued response</FormLabel>
            <Input value={value.dataQueuedResponse} onChange={e => upd('dataQueuedResponse', e.target.value)} />
            <FormHelperText>Placeholder: <code>{'{id}'}</code> (fake Postfix-style queue ID).</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>STARTTLS response</FormLabel>
            <Input value={value.startTlsResponse} onChange={e => upd('startTlsResponse', e.target.value)} />
          </FormControl>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ width: 120 }}>
              <FormLabel>VRFY code</FormLabel>
              <Input type="number" value={value.vrfyCode} onChange={e => upd('vrfyCode', Number(e.target.value))} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>VRFY message</FormLabel>
              <Input value={value.vrfyMessage} onChange={e => upd('vrfyMessage', e.target.value)} />
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ width: 120 }}>
              <FormLabel>HELP code</FormLabel>
              <Input type="number" value={value.helpCode} onChange={e => upd('helpCode', Number(e.target.value))} />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>HELP message</FormLabel>
              <Input value={value.helpMessage} onChange={e => upd('helpMessage', e.target.value)} />
            </FormControl>
          </Stack>

          <FormControl>
            <FormLabel>Pre-greet talker response</FormLabel>
            <Input value={value.tooSoonResponse} onChange={e => upd('tooSoonResponse', e.target.value)} />
          </FormControl>

          <FormControl>
            <FormLabel>Unknown command response</FormLabel>
            <Input value={value.unknownCommandResponse} onChange={e => upd('unknownCommandResponse', e.target.value)} />
          </FormControl>

          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <FormLabel>Hide XCLIENT / XFORWARD advertisements</FormLabel>
              <FormHelperText>Recommended — these EHLO capabilities are smtp-server-specific.</FormHelperText>
            </Box>
            <Switch checked={value.hideXClient} onChange={e => upd('hideXClient', e.target.checked)} />
          </FormControl>
        </Stack>
      )}
    </Stack>
  );
}
